import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { isSupabaseConfigured, loadAuthData, saveAuthData, appendAuthLog, fetchAuthUsers } from '../supabase/dbService';
import { isEdgeMode } from '../supabase/config';
import { edgeApi, setEdgeToken, clearEdgeToken } from '../supabase/edgeApi';
import { ALL_MODULES, ALL_MODULE_KEYS } from '../menuConfig';
import { getTenantId } from '../tenant';

const AuthContext = createContext();

// Storage keys are scoped PER TENANT so one company's cached session or user
// list can never surface under another company's subdomain. (Different
// subdomains are already separate origins, but scoping also protects a shared
// origin and a stale session left over from switching tenants.)
const authKey = () => `erp_auth_data_${getTenantId()}`;
const sessionKey = () => `erp_session_${getTenantId()}`;

// The signed session token carries its tenant in the payload ("t"). We read it
// (without verifying — the server still verifies the signature) purely to make
// sure a restored session belongs to the tenant we're on right now.
function tokenTenant(token) {
  try {
    const body = String(token).split('.')[0];
    const obj = JSON.parse(atob(body.replace(/-/g, '+').replace(/_/g, '/')));
    return obj && obj.t ? String(obj.t) : null;
  } catch { return null; }
}

// Re-exported so existing importers (e.g. UserManagement) keep working.
// The list is derived from the single shared menu config in menuConfig.js, so
// any module added there shows up here (and in User Management) automatically.
export { ALL_MODULES, ALL_MODULE_KEYS };

export const ROLE_TEMPLATES = {
  Admin: ALL_MODULE_KEYS,
  Accountant: [
    'dashboard', 'parties', 'accounts', 'taxes',
    'sales-invoice', 'sales-return', 'purchase-bill', 'purchase-return',
    'receipt', 'payment', 'journal', 'expense', 'contra', 'bank-reconcile',
    'report-sales', 'report-purchase', 'report-accounts',
  ],
  Salesperson: [
    'dashboard', 'parties', 'items',
    'sales-invoice', 'sales-return', 'delivery-challan', 'sales-order',
    'stock-summary', 'low-stock',
    'receipt',
    'report-sales',
  ],
  'Purchase Manager': [
    'dashboard', 'parties', 'items',
    'purchase-bill', 'purchase-return', 'purchase-order',
    'stock-summary', 'stock-ledger', 'low-stock',
    'payment',
    'report-purchase', 'report-inventory',
  ],
  'Inventory Manager': [
    'dashboard', 'items',
    'stock-summary', 'stock-ledger', 'stock-journal', 'low-stock',
    'delivery-challan',
    'report-inventory',
  ],
  Viewer: [
    'dashboard',
    'report-sales', 'report-purchase', 'report-inventory', 'report-accounts',
  ],
};

function hashPassword(password) {
  if (!password) return '';
  let hash = 0;
  for (let i = 0; i < password.length; i++) {
    const char = password.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash |= 0;
  }
  return 'h_' + Math.abs(hash).toString(36);
}

const defaultAdmin = {
  id: 'admin-default',
  username: 'admin',
  password: hashPassword('admin123'),
  fullName: 'Administrator',
  email: 'admin@company.com',
  role: 'Admin',
  modules: ALL_MODULE_KEYS,
  isActive: true,
  createdAt: new Date().toISOString(),
  lastLogin: null,
};

function ensureAdmin(usersList) {
  const hasAdmin = usersList?.some(u => u.id === 'admin-default');
  if (!hasAdmin) {
    return [defaultAdmin, ...(usersList || [])];
  }
  return usersList.map(u =>
    u.id === 'admin-default' ? { ...u, modules: ALL_MODULE_KEYS, role: 'Admin' } : u
  );
}

function loadLocalAuthData() {
  try {
    const saved = localStorage.getItem(authKey());
    if (saved) {
      const data = JSON.parse(saved);
      return { users: ensureAdmin(data.users) };
    }
  } catch (e) {
    console.error('Failed to load auth data:', e);
  }
  return null;
}

function loadSession() {
  try {
    const s = localStorage.getItem(sessionKey());
    if (!s) return null;
    const parsed = JSON.parse(s);
    // Data-isolation guard: never trust a session whose token was issued for a
    // different tenant (e.g. an embercore token lingering on the mayasoft side).
    const tt = tokenTenant(parsed?.token);
    if (tt && tt !== getTenantId()) {
      try { localStorage.removeItem(sessionKey()); } catch { /* ignore */ }
      return null;
    }
    return parsed;
  } catch (e) { /* ignore */ }
  return null;
}

export function AuthProvider({ children }) {
  const [users, setUsers] = useState(() => {
    const data = loadLocalAuthData();
    return data?.users || [defaultAdmin];
  });
  const [currentUser, setCurrentUser] = useState(null);
  const [sessionToken, setSessionToken] = useState(() => loadSession()?.token || null);
  const [authLoading, setAuthLoading] = useState(true);
  const [authInitialized, setAuthInitialized] = useState(false);

  useEffect(() => {
    async function init() {
      // ── Edge mode: the DB is locked, so we can't read users before login.
      //    Restore the session purely from the signed token, then rehydrate
      //    the user list from the server (which the token authorizes). ──────
      if (isEdgeMode()) {
        const session = loadSession();
        if (session?.token && session?.userId) {
          setEdgeToken(session.token);
          try {
            const data = await loadAuthData();               // authorized by the token
            const list = ensureAdmin(data?.users || []);
            const user = list.find(u => u.id === session.userId && u.isActive);
            if (user) {
              setUsers(list);
              setCurrentUser(user);
              setSessionToken(session.token);
            } else {
              clearEdgeToken();
              try { localStorage.removeItem(sessionKey()); } catch { /* ignore */ }
            }
          } catch {
            // token expired / invalid → force a fresh login
            clearEdgeToken();
            try { localStorage.removeItem(sessionKey()); } catch { /* ignore */ }
          }
        }
        setAuthLoading(false);
        setAuthInitialized(true);
        return;
      }

      let loadedUsers = null;

      if (isSupabaseConfigured()) {
        const data = await loadAuthData();
        if (data?.users) {
          loadedUsers = ensureAdmin(data.users);
        }
      }

      if (loadedUsers) {
        setUsers(loadedUsers);
      }

      const finalUsers = loadedUsers || users;
      const session = loadSession();
      if (session?.userId && session?.token) {
        const user = finalUsers.find(u => u.id === session.userId && u.isActive);
        // Single-session: only restore if this device still holds the active session.
        if (user && (!user.activeSession || user.activeSession === session.token)) {
          setCurrentUser(user);
        } else {
          setSessionToken(null);
          try { localStorage.removeItem(sessionKey()); } catch { /* ignore */ }
        }
      }

      setAuthLoading(false);
      setAuthInitialized(true);
    }
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!authInitialized) return;
    // Edge mode persists the user list explicitly on admin actions (add/update/
    // delete) — NOT reactively — because login/logout mutate activeSession
    // server-side and a blanket save could clobber it. See addUser/updateUser.
    if (isEdgeMode()) return;
    if (isSupabaseConfigured()) {
      saveAuthData(users);
    } else {
      localStorage.setItem(authKey(), JSON.stringify({ users }));
    }
  }, [users, authInitialized]);

  useEffect(() => {
    if (!authInitialized) return;
    if (currentUser && sessionToken) {
      localStorage.setItem(sessionKey(), JSON.stringify({ userId: currentUser.id, token: sessionToken }));
    } else {
      localStorage.removeItem(sessionKey());
    }
  }, [currentUser, sessionToken, authInitialized]);

  // Single-session enforcement: if another device logs in with the same account,
  // its login overwrites activeSession in Supabase; this device detects the mismatch
  // (on focus + every 45s) and signs out.
  const validateSession = useCallback(async () => {
    if (!currentUser || !sessionToken || !isSupabaseConfigured()) return;
    let signedOut = false;
    if (isEdgeMode()) {
      // Server compares this token's session id against the stored active one.
      try {
        const { valid } = await edgeApi.validateSession();
        signedOut = valid === false;
      } catch (e) {
        // 401 → token expired/invalid → sign out
        if (e.status === 401) signedOut = true; else return;
      }
    } else {
      const freshUsers = await fetchAuthUsers();
      if (!freshUsers) return;
      const fresh = freshUsers.find(u => u.id === currentUser.id);
      signedOut = !!(fresh && fresh.activeSession && fresh.activeSession !== sessionToken);
    }
    if (signedOut) {
      clearEdgeToken();
      setCurrentUser(null);
      setSessionToken(null);
      try { localStorage.removeItem(sessionKey()); } catch { /* ignore */ }
      if (typeof window !== 'undefined') {
        window.alert('You have been signed out because this account was logged in on another device.');
      }
    }
  }, [currentUser, sessionToken]);

  useEffect(() => {
    if (!currentUser) return;
    const tick = () => { validateSession(); };
    const id = setInterval(tick, 45000);
    window.addEventListener('focus', tick);
    return () => { clearInterval(id); window.removeEventListener('focus', tick); };
  }, [currentUser, validateSession]);

  const login = useCallback(async (username, password) => {
    // ── Edge mode: the server verifies credentials and issues a signed token.
    //    The browser never sees other users' password hashes. ──────────────
    if (isEdgeMode()) {
      try {
        const res = await edgeApi.login(username, password);   // throws on bad creds
        setEdgeToken(res.token);
        // Rehydrate the full user list (now authorized by the fresh token).
        let list = ensureAdmin([res.user]);
        try { const data = await loadAuthData(); if (data?.users) list = ensureAdmin(data.users); } catch { /* ignore */ }
        setUsers(list);
        setCurrentUser(res.user);
        setSessionToken(res.token);
        return { success: true };
      } catch (e) {
        return { success: false, error: e.message || 'Login failed' };
      }
    }

    const user = users.find(
      u => u.username.toLowerCase() === username.toLowerCase() && u.password === hashPassword(password)
    );
    if (!user) return { success: false, error: 'Invalid username or password' };
    if (!user.isActive) return { success: false, error: 'This account has been deactivated. Contact your administrator.' };

    const token = uuidv4();
    const now = new Date().toISOString();
    // Stamp this token as the account's single active session (overrides any other device).
    const updated = { ...user, lastLogin: now, activeSession: token, activeSessionAt: now };
    setUsers(prev => prev.map(u => u.id === user.id ? updated : u));
    setCurrentUser(updated);
    setSessionToken(token);
    appendAuthLog({
      id: uuidv4(), userId: user.id, username: user.username, action: 'login',
      at: now, device: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    });
    return { success: true };
  }, [users]);

  const logout = useCallback(() => {
    if (isEdgeMode()) {
      // Server clears activeSession + writes the logout audit entry.
      edgeApi.logout().catch(() => { /* best effort */ });
      clearEdgeToken();
      setCurrentUser(null);
      setSessionToken(null);
      localStorage.removeItem(sessionKey());
      return;
    }
    if (currentUser) {
      appendAuthLog({
        id: uuidv4(), userId: currentUser.id, username: currentUser.username, action: 'logout',
        at: new Date().toISOString(), device: typeof navigator !== 'undefined' ? navigator.userAgent : '',
      });
      // Release the active session only if this device owns it.
      setUsers(prev => prev.map(u => (u.id === currentUser.id && u.activeSession === sessionToken) ? { ...u, activeSession: null } : u));
    }
    setCurrentUser(null);
    setSessionToken(null);
    localStorage.removeItem(sessionKey());
  }, [currentUser, sessionToken]);

  const addUser = useCallback((userData) => {
    if (users.some(u => u.username.toLowerCase() === userData.username.toLowerCase())) {
      return { success: false, error: 'Username already exists' };
    }
    const newUser = {
      id: uuidv4(),
      ...userData,
      password: hashPassword(userData.password),
      isActive: true,
      createdAt: new Date().toISOString(),
      lastLogin: null,
    };
    const next = [...users, newUser];
    setUsers(next);
    if (isEdgeMode()) saveAuthData(next);   // explicit persist (reactive save is off in edge mode)
    return { success: true, user: newUser };
  }, [users]);

  const updateUser = useCallback((id, userData) => {
    if (id === 'admin-default' && userData.isActive === false) {
      return { success: false, error: 'Cannot deactivate the default admin account' };
    }
    if (id === 'admin-default' && userData.role && userData.role !== 'Admin') {
      return { success: false, error: 'Cannot change the default admin role' };
    }
    if (userData.username && users.some(u => u.id !== id && u.username.toLowerCase() === userData.username.toLowerCase())) {
      return { success: false, error: 'Username already exists' };
    }
    const updatedData = { ...userData };
    if (updatedData.password) {
      updatedData.password = hashPassword(updatedData.password);
    }
    const next = users.map(u => u.id === id ? { ...u, ...updatedData } : u);
    setUsers(next);
    if (isEdgeMode()) saveAuthData(next);   // explicit persist (reactive save is off in edge mode)
    if (currentUser?.id === id) {
      setCurrentUser(prev => ({ ...prev, ...updatedData }));
    }
    return { success: true };
  }, [users, currentUser]);

  const deleteUser = useCallback((id) => {
    if (id === 'admin-default') {
      return { success: false, error: 'Cannot delete the default admin account' };
    }
    if (currentUser?.id === id) {
      return { success: false, error: 'Cannot delete your own account while logged in' };
    }
    const next = users.filter(u => u.id !== id);
    setUsers(next);
    if (isEdgeMode()) saveAuthData(next);   // explicit persist (reactive save is off in edge mode)
    return { success: true };
  }, [users, currentUser]);

  const hasAccess = useCallback((moduleKey) => {
    if (!currentUser) return false;
    if (currentUser.role === 'Admin') return true;
    return currentUser.modules?.includes(moduleKey) || false;
  }, [currentUser]);

  const isAdmin = currentUser?.role === 'Admin';

  return (
    <AuthContext.Provider value={{
      currentUser,
      users,
      isLoggedIn: !!currentUser,
      isAdmin,
      authLoading,
      login,
      logout,
      addUser,
      updateUser,
      deleteUser,
      hasAccess,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
